/**
 * Hoja "Instala Fermin" (brief §6, adaptado): diálogo modal inferior con la guía animada.
 *
 * - Android/Chromium con `beforeinstallprompt`: botón "Instalar" que llama a `prompt()`; debajo,
 *   "¿No funciona? Hazlo desde el menú del navegador" despliega la animación de Android.
 * - iOS (nunca hay prompt nativo) o Android sin evento: la animación directamente.
 * - Escritorio: selector iPhone iOS 26 / iOS 18 / Android para ver la guía.
 * - Tras `appinstalled` o un `prompt()` aceptado: "Listo".
 *
 * En Fermin la instalación es un paso explícito: esta hoja **nunca se abre sola** (ni por tiempo ni
 * por páginas vistas); la abren la card de Inicio, "Cómo instalar la app" en Perfil o
 * `?instalar=<variante>` en QA. El diálogo (`role="dialog"`, `aria-modal`, foco atrapado, Esc,
 * bloqueo de scroll, foco devuelto al cerrar, safe area inferior) lo da `components/ui/sheet.tsx`.
 */
import { ChevronDown, TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Button } from '../../components/ui/button';
import { cn } from '../../components/ui/cn';
import { Sheet } from '../../components/ui/sheet';
import { t } from '../../i18n';

import { InstallGuideLazy } from './install-guide-lazy';
import { useInstallPrompt } from './use-install-prompt';
import { APP_NAME, currentHost, detectDevice, variantFor, type VariantId } from './variants';

const PREVIEW: VariantId[] = ['ios26', 'ios18', 'android'];

export interface InstallSheetProps {
  open: boolean;
  onClose: () => void;
  /** Variante forzada (`?instalar=…` o "¿No funciona?" en Android). */
  forced?: VariantId;
  /** Abre con los pasos manuales desplegados aunque haya prompt nativo. */
  manual?: boolean;
  /** Dispositivo ya registrado: en iPhone se avisa de la re-vinculación. */
  enrolled?: boolean;
}

export function InstallSheet({
  open,
  onClose,
  forced,
  manual = false,
  enrolled = false,
}: InstallSheetProps) {
  const device = useMemo(() => detectDevice(), []);
  const { canPrompt, installed, prompt } = useInstallPrompt();
  const [preview, setPreview] = useState<VariantId>('ios26');
  const [showManual, setShowManual] = useState(manual);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const guideId = useId();

  const chooser = device.platform === 'other' && !forced;
  const variantId: VariantId =
    forced ?? (device.platform === 'other' ? preview : variantFor(device));
  // Una variante iOS forzada (`?instalar=ios26`, QA en escritorio) muestra su animación aunque el
  // navegador ofrezca el prompt nativo; en un iPhone real ese evento no existe.
  const native = canPrompt && device.platform !== 'ios' && !(forced && forced !== 'android');
  const done = accepted || installed;
  const guideVisible = !done && (!native || showManual);
  const iosRebind = enrolled && device.platform === 'ios';

  async function install(): Promise<void> {
    setBusy(true);
    try {
      if ((await prompt()) === 'accepted') {
        setAccepted(true);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      className="md:mx-auto md:w-full md:max-w-[440px]"
      title={
        done
          ? t('install.sheet.doneTitle', { app: APP_NAME })
          : t('install.sheet.title', { app: APP_NAME })
      }
      description={done ? t('install.sheet.doneLead') : t('install.sheet.lead')}
      footer={
        <Button data-testid="install-sheet-close" variant="secondary" onClick={onClose}>
          {t('common.buttons.understood')}
        </Button>
      }
    >
      <div data-testid="install-sheet" className="flex flex-col gap-fm-4">
        {chooser && !done ? (
          <div
            role="radiogroup"
            aria-label={t('install.sheet.previewLabel')}
            className="mx-auto inline-flex rounded-fm-pill border border-fm-border p-0.5"
          >
            {PREVIEW.map((id) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={preview === id}
                onClick={() => setPreview(id)}
                className={cn(
                  'min-h-11 rounded-fm-pill px-fm-3 text-fm-sm font-semibold transition-colors duration-fm-fast',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
                  preview === id ? 'bg-fm-text text-fm-text-inverse' : 'text-fm-text-secondary',
                )}
              >
                {t(`install.sheet.preview.${id}`)}
              </button>
            ))}
          </div>
        ) : null}

        {native && !done ? (
          <div className="flex flex-col gap-fm-3">
            <Button
              data-testid="install-sheet-install"
              onClick={() => void install()}
              loading={busy}
              loadingText={t('install.sheet.installing')}
            >
              {t('install.action')}
            </Button>
            <p className="text-center text-fm-sm text-fm-text-secondary">
              {t('install.sheet.nativeHint')}
            </p>
            <button
              type="button"
              data-testid="install-sheet-manual-toggle"
              onClick={() => setShowManual(!showManual)}
              aria-expanded={showManual}
              aria-controls={guideId}
              className="mx-auto inline-flex min-h-11 items-center gap-fm-1 font-ui text-fm-base font-semibold text-fm-primary-compact underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
            >
              {showManual ? t('install.sheet.hideSteps') : t('install.manualHelp')}
              <ChevronDown
                aria-hidden="true"
                size={18}
                className={cn(
                  'transition-transform duration-fm-fast motion-reduce:transition-none',
                  showManual && 'rotate-180',
                )}
              />
            </button>
          </div>
        ) : null}

        {guideVisible ? (
          <div id={guideId}>
            <InstallGuideLazy
              variantId={native ? 'android' : variantId}
              appName={APP_NAME}
              host={currentHost()}
              info={device}
            />
          </div>
        ) : null}

        {iosRebind && !done ? (
          <p
            data-testid="install-sheet-ios-rebind"
            className="flex items-start gap-fm-2 text-fm-sm text-fm-text-secondary"
          >
            <TriangleAlert aria-hidden="true" size={16} className="mt-fm-1 shrink-0" />
            {t('install.sheet.iosRebind')}
          </p>
        ) : null}

        {done ? (
          <p
            data-testid="install-sheet-done"
            role="status"
            className="text-fm-base text-fm-success-text"
          >
            {t('install.installed', { app: APP_NAME })}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
